import React from 'react'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import { checkIfCategoryExists, getProjects } from '@/helpers/api'
import { ListRouterQuery } from '@/interfaces/filters'
import { Project } from '@/interfaces/projects'
import { BreadcrumbItem, ListPageProps } from '@/interfaces/pages'
import { checkIfShouldRedirectCategory, getCategoryInfo } from '@/helpers/categories'
import useQueryParamsHandler from '@/hooks/useQueryParamsHandler'
import { isMobileDevice, isNil } from '@/helpers/helpers'
import { SeoHead } from '@/components/seo/SeoHead'
import { getCanonicalLink } from '@/helpers/seo'
import { getFiltersFromQuery } from '@/helpers/filters'
import ProjectsViewWrapper from '@/components/projects/ProjectsViewWrapper'
import Subheader from '@/components/subheader/Subheader'

interface CategoryPageProps extends ListPageProps {
  categoryTitle: string
  projects: Project[]
}

export async function getServerSideProps(
  context: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<CategoryPageProps>> {
  const params = context.query as ListRouterQuery

  const categoryExists = await checkIfCategoryExists(params?.categoryId?.toUpperCase(), context.req)
  const { shouldRedirect: shouldRedirectCategory, targetUrl } = checkIfShouldRedirectCategory(
    params?.categoryId?.toUpperCase(),
    context.resolvedUrl
  )
  if (shouldRedirectCategory) {
    return {
      redirect: {
        destination: targetUrl,
        permanent: true
      }
    }
  }
  if (!categoryExists) {
    return {
      notFound: true
    }
  }

  const category = getCategoryInfo(params?.categoryId?.toUpperCase(), true)

  const { filters, shouldRedirect: shouldRedirectParams } = getFiltersFromQuery(params, context.req)

  if (shouldRedirectParams) {
    const filtersQueryString = Object.keys(filters)
      .map((key) => `${key}=${filters[key]}`)
      .join('&')
      .toString()
    return {
      redirect: {
        destination: `/categories/${params?.categoryId}/?${filtersQueryString}`,
        permanent: false
      }
    }
  }

  const { items: projects, pagingMetadata, error } = await getProjects({ ...params, ...filters }, context.req)

  if (!isNil(error)) {
    context.res.statusCode = error.status || 500
    throw new Error(`Error fetching projects: ${error.message} - ${JSON.stringify(error)}`)
  }

  if (+params?.page <= 0 || (pagingMetadata?.totalPages > 0 && +params?.page > pagingMetadata?.totalPages)) {
    return {
      notFound: true
    }
  }

  const categoryTitle = category.friendlyName
  const title: string =
    categoryTitle + ' | SimScale Project Library' + (pagingMetadata?.page ? ` | Page ${pagingMetadata?.page}` : '')
  const description =
    (!isNil(category.description)
      ? category.description
      : 'SimScale gives you access to Public Projects, a library of free simulations and validations with a detailed setup and results that you can use as templates.') +
    (pagingMetadata?.page ? ` | Page ${pagingMetadata?.page}` : '')

  const canonicalLink = getCanonicalLink(`/projects/categories/${params?.categoryId}/`, { ...params, ...filters })

  const breadcrumbs: BreadcrumbItem[] = [
    {
      label: 'SimScale Project Library',
      href: '/'
    },
    {
      label: categoryTitle
    }
  ]

  const isMobile = isMobileDevice(context.req?.headers['user-agent'] || '')

  return {
    props: {
      title,
      description,
      breadcrumbs,
      categoryTitle,
      projects,
      pagingMetadata,
      canonicalLink,
      isMobile,
      ...params,
      ...filters,
      ...(await serverSideTranslations(context.locale, ['common', 'projects', 'header', 'subheader', 'filters']))
    }
  }
}

export default function CategoryPage({
  title,
  description,
  breadcrumbs,
  categoryTitle,
  projects,
  viewMode,
  sortBy,
  time,
  search,
  pagingMetadata,
  canonicalLink,
  isMobile
}: CategoryPageProps): React.ReactElement {
  useQueryParamsHandler()

  return (
    <>
      <SeoHead title={title} description={description} canonicalLink={canonicalLink} />
      <Subheader
        title={categoryTitle}
        breadcrumbs={breadcrumbs}
        viewMode={viewMode}
        sortByFilter={sortBy}
        timeFilter={time}
        searchString={search}
        showSearch
        showSortByFilters
        showTimeFilters
        showViewMode={!isMobile}
      />
      <Container>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <ProjectsViewWrapper
              projects={projects}
              viewMode={isMobile ? 'listView' : viewMode}
              gridColumns={4}
              pagingMetadata={pagingMetadata}
              showCreateMessage
            />
          </Grid>
        </Grid>
      </Container>
    </>
  )
}
